import { Component, inject, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FocusTrapModule } from 'primeng/focustrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { ModalDialog } from '../../../../shared/directives/modal-dialog';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

interface GovernanceMember {
  photo: string;
  badgeLabel: string;
  name: string;
  role: string;
  description: string;
}

interface KeyFigure {
  value: string;
  label: string;
}

interface Mission {
  icon: string;
  bg: string;
  fg: string;
  title: string;
  description: string;
}

interface FlagshipEventActivity {
  title: string;
  points: string[];
}

interface FlagshipEvent {
  accent: string;
  title: string;
  description: string;
  /** Contenu détaillé affiché dans la modale, en plus de `description` (voir openFlagshipEvent). */
  objectives: string[];
  activities: FlagshipEventActivity[];
  impactIntro?: string;
  impact: string[];
  whyParticipate: string[];
}

interface Objective {
  number: string;
  accent: string;
  title: string;
  description: string;
}

/** Contenu traduit (public/i18n/*.json, clé `pages.about.content`) fusionné avec les métadonnées visuelles ci-dessous. */
interface AboutContent {
  governance: Pick<GovernanceMember, 'badgeLabel' | 'name' | 'role' | 'description'>[];
  keyFigures: KeyFigure[];
  missions: Pick<Mission, 'title' | 'description'>[];
  flagshipEvents: Pick<FlagshipEvent, 'title' | 'description' | 'objectives' | 'activities' | 'impactIntro' | 'impact' | 'whyParticipate'>[];
  objectives: Pick<Objective, 'title' | 'description'>[];
}

/** Photo plein cadre du bandeau d'introduction — purement décorative, à ajuster/remplacer ici au besoin. */
const HERO_IMAGE = 'assets/images/pic/image00045.jpeg';

/** Métadonnées visuelles fixes (indépendantes de la langue) — le texte vient de `pages.about.content` (public/i18n/*.json). */
const GOVERNANCE_META: Pick<GovernanceMember, 'photo'>[] = [
  { photo: 'assets/images/ministre-minjec.png' },
  { photo: 'assets/images/directeur-national.png' },
];

const MISSIONS_META: Pick<Mission, 'icon' | 'bg' | 'fg'>[] = [
  { icon: 'pi-compass', bg: '#EAF0FF', fg: '#2563EB' },
  { icon: 'pi-sitemap', bg: '#E9F4E4', fg: '#3A7A2B' },
  { icon: 'pi-chart-bar', bg: '#FAE4E7', fg: '#B01D30' },
  { icon: 'pi-share-alt', bg: '#EEF0F2', fg: '#4B5563' },
  { icon: 'pi-heart', bg: '#FBF3DE', fg: '#8A6414' },
  { icon: 'pi-megaphone', bg: '#E4F3FA', fg: '#0678AC' },
];

const FLAGSHIP_EVENTS_META: Pick<FlagshipEvent, 'accent'>[] = [
  { accent: 'var(--ycc-rouge)' },
  { accent: 'var(--ycc-bleu-ciel)' },
  { accent: 'var(--ycc-vert)' },
  { accent: 'var(--cmr-jaune)' },
];

const OBJECTIVES_META: Pick<Objective, 'number' | 'accent'>[] = [
  { number: '01', accent: 'var(--ycc-rouge)' },
  { number: '02', accent: 'var(--pnud-bleu)' },
  { number: '03', accent: 'var(--ycc-vert)' },
];

@Component({
  selector: 'app-about',
  imports: [RouterLink, NgOptimizedImage, FocusTrapModule, ModalDialog, ScrollReveal, TranslatePipe],
  templateUrl: './about.html',
  styleUrl: './about.css',
})
export class About {
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);

  readonly isAuthenticated = this.auth.isAuthenticated;

  readonly heroImage = HERO_IMAGE;
  readonly governance = signal<GovernanceMember[]>([]);
  readonly keyFigures = signal<KeyFigure[]>([]);
  readonly missions = signal<Mission[]>([]);
  readonly flagshipEvents = signal<FlagshipEvent[]>([]);
  readonly objectives = signal<Objective[]>([]);

  /** Événement dont la modale de détail est ouverte (voir la section "Événements phares" du template). */
  readonly selectedFlagshipEvent = signal<FlagshipEvent | null>(null);

  constructor() {
    this.loadContent();
    this.translate.onLangChange.subscribe(() => this.loadContent());
  }

  private loadContent(): void {
    this.translate.get('pages.about.content').subscribe((content: AboutContent) => {
      this.governance.set(GOVERNANCE_META.map((meta, i) => ({ ...meta, ...content.governance[i] })));
      this.keyFigures.set(content.keyFigures);
      this.missions.set(MISSIONS_META.map((meta, i) => ({ ...meta, ...content.missions[i] })));
      this.flagshipEvents.set(FLAGSHIP_EVENTS_META.map((meta, i) => ({ ...meta, ...content.flagshipEvents[i] })));
      this.objectives.set(OBJECTIVES_META.map((meta, i) => ({ ...meta, ...content.objectives[i] })));
    });
  }

  openFlagshipEvent(event: FlagshipEvent): void {
    this.selectedFlagshipEvent.set(event);
  }

  closeFlagshipEvent(): void {
    this.selectedFlagshipEvent.set(null);
  }
}
